import React, { useCallback, useMemo, useRef } from "react";
import { Figure } from "../Figure";
import { CategoryChart, LongText, prefixValue, suffixValue, Unit } from "../processors";
import { FigureHorStackBarKpiProviders } from "../categoryChartQueryFns";
import { Paragraph } from "../Paragraph";
import { useCategoryContext } from "../Context";
import { CategoryTheme, PROVIDER_TITLE_CLASS } from "../misc";
import { getHighlightColors } from "../../themes";
import styles from "./HorizontalChartProviders.module.scss";
import _ from "lodash";
import {
    HEIGHT_UPDATE_REQUIRE_CLASS_NAME,
    HEIGHT_WATCHING_CLASS_NAME,
    HorizontalChartWithData,
    WIDTH_LABEL_UPDATE_CLASS_NAME,
    WIDTH_LABEL_WATCHING_CLASS_NAME
} from "../HorizontalLine";

export interface HorizontalChartProvidersProps extends FigureHorStackBarKpiProviders {
    categoryChart: CategoryChart;
}

export const HorizontalChartProviders: React.FC<HorizontalChartProvidersProps> = ({
    categoryChart,
    unit,
    waterProviders,
    title,
    elements: ___elements
}) => {
    const divRef = useRef<HTMLDivElement>();

    const { category } = useCategoryContext();
    const theme = category.theme as CategoryTheme;

    const __elements = useMemo(() => {
        // return ___elements?.map((element, index) => {
        //     let data = element?.graph?.data;

        //     data =
        //         index === 1
        //             ? data?.map((d, tindex) => {
        //                   return {
        //                       label: d.label,
        //                       value: tindex > 0 ? d.value : (`NR` as unknown as number),
        //                       seriesName: ""
        //                   };
        //               })
        //             : data;

        //     return {
        //         ...element,
        //         graph: {
        //             ...element?.graph,
        //             data
        //         }
        //     };
        // });

        return ___elements;
    }, [___elements]);

    const queryHeightWatchingElements: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    } = useCallback(() => {
        const watching = Array.from(
            divRef?.current?.querySelectorAll(HEIGHT_WATCHING_CLASS_NAME) as NodeListOf<HTMLElement>
        );
        const update = Array.from(
            divRef?.current?.querySelectorAll(HEIGHT_UPDATE_REQUIRE_CLASS_NAME) as NodeListOf<HTMLElement>
        );
        return {
            watching,
            update
        };
    }, []);

    const queryLabelWidthWatchingElements: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    } = useCallback(() => {
        const watching = Array.from(
            divRef?.current?.querySelectorAll(WIDTH_LABEL_WATCHING_CLASS_NAME) as NodeListOf<HTMLElement>
        );
        const update = Array.from(
            divRef?.current?.querySelectorAll(WIDTH_LABEL_UPDATE_CLASS_NAME) as NodeListOf<HTMLElement>
        );
        return {
            watching,
            update
        };
    }, []);

    const hasAtLeastOneWithNoData = useMemo(() => {
        const noDataRecords = __elements.map(element => {
            return (
                (element?.figure?.value != null && typeof element?.figure?.value === "string") ||
                element?.graph?.data?.find(d => d.value != null && d.value !== 0 && typeof d.value !== "string") == null
            );
        });

        return noDataRecords?.find(d => d === true) != null;
    }, [__elements]);

    // sort data, each one if it doesn't exist from the other, add 0
    const _elements = useMemo(() => {
        const labels = _.uniq(
            _.flattenDeep(
                __elements?.map(element =>
                    element?.graph?.data
                        ?.filter(d => d.value != null && d.value !== 0 && typeof d.value !== "string")
                        ?.map(d => d.label)
                )
            )
        );

        const newElements = __elements?.map(element => {
            const data = element?.graph?.data;

            const descData = _.orderBy(
                data?.filter(d => d?.value != null && d?.value !== 0 && typeof d.value !== "string"),
                d => d.value,
                "desc"
            );
            const descLabels = descData
                ?.map(d => d.label)
                ?.reduce((prev, curr) => {
                    prev[curr] = 1;
                    return prev;
                }, {});

            const zeroData = labels
                ?.filter(label => descLabels[label] !== 1)
                ?.map(label => {
                    return {
                        label,
                        value: 0,
                        seriesName: ""
                    };
                });
            const newData = [...descData, ...zeroData];

            return {
                ...element,
                graph: {
                    ...element?.graph,
                    data: newData
                }
            };
        });

        return newElements;
    }, [__elements]);

    // sort data, each one if it doesn't exist from the other, add 0
    const maxValue = useMemo(() => {
        return _.max(
            _.flattenDeep(
                _elements?.map(d => d?.graph?.data?.map(d => (typeof d.value === "string" ? 0 : d?.value ?? 0)))
            )
        );
    }, [_elements]);

    return (
        <div className="row" ref={divRef}>
            {title != null && <Paragraph title={""} status={true} nid={0} text={title} className="col-12" />}

            {_elements?.map((element, index) => {
                const isNoData =
                    (element?.figure?.value != null && typeof element?.figure?.value === "string") ||
                    element?.graph?.data?.find(d => d.value != null && d.value !== 0 && typeof d.value !== "string") ==
                        null;

                const figure: {
                    unit?: Unit;
                    header?: LongText;
                    value: string | number;
                    decimals: number;
                    footer?: LongText;
                } = !isNoData
                    ? null
                    : {
                          unit: typeof element?.figure?.value === "string" ? null : unit,
                          header: null,
                          value:
                              typeof element?.figure?.value === "string"
                                  ? element?.figure?.value
                                  : // : VALUE_STATUS_NAMING.NA,
                                    0,
                          decimals: 0,
                          footer: null
                      };

                return (
                    element != null &&
                    waterProviders?.[index] != null && (
                        <div
                            className={`col-12 ${`col-lg-${12 / _elements?.length}`} ${styles.chartElement}`}
                            key={index}
                        >
                            {_elements?.length > 1 && (
                                <div className="headline">
                                    <div className={`${styles.providerTitle} ${PROVIDER_TITLE_CLASS}`}>
                                        {waterProviders[index].name}
                                    </div>
                                </div>
                            )}

                            {element?.graph?.header && (
                                <Paragraph
                                    nid={0}
                                    status={true}
                                    title={""}
                                    text={element?.graph?.header}
                                    className={`marginBottom headline 
                                        chart-headline ${
                                            // in comparison, centralise the chart headline
                                            _elements?.length > 1 ? styles.center : ""
                                        }`}
                                />
                            )}

                            {figure != null && (
                                <Figure
                                    figureStyle={{
                                        // getting colors for water provider
                                        color: getHighlightColors(theme, index + 1)?.[index]
                                    }}
                                    className="w-100"
                                    decimals={figure.decimals}
                                    header={
                                        !figure.header ? null : (
                                            <Paragraph title={""} status={true} nid={0} text={figure.header} />
                                        )
                                    }
                                    footer={
                                        !figure.footer ? null : (
                                            <Paragraph title={""} status={true} nid={0} text={figure.footer} />
                                        )
                                    }
                                    value={figure.value}
                                    prefix={prefixValue(figure.value, figure.unit)}
                                    suffix={suffixValue(figure.value, figure.unit)}
                                    unitInSuffix={figure?.unit}
                                    unit={figure?.unit}
                                    categoryChart={categoryChart}
                                />
                            )}

                            {figure == null && (
                                <HorizontalChartWithData
                                    maxValue={maxValue}
                                    annotationPrefix={prefixValue(0, unit)}
                                    annotationSuffix={suffixValue(0, unit)}
                                    data={element?.graph?.data}
                                    theme={theme}
                                    queryHeightWatchingElements={queryHeightWatchingElements}
                                    queryLabelWidthWatchingElements={queryLabelWidthWatchingElements}
                                    enableLabelWidthWatching={
                                        _elements?.length === 1 || hasAtLeastOneWithNoData ? false : true
                                    }
                                    className={"marginBottom"}
                                />
                            )}

                            {element?.graph?.footer && (
                                <Paragraph
                                    nid={0}
                                    status={true}
                                    title={""}
                                    text={element?.graph?.footer}
                                    className={`marginBottom`}
                                />
                            )}
                        </div>
                    )
                );
            })}
        </div>
    );
};
